import { makeAudioContext } from '../utils/make-audio-context';
import { LibSampleRateSrc } from '../worklets/lib-sample-rate';
import { PcmAudioWorkletSrc } from '../worklets/pcm-audio-worklet';
import { type BrowserAudioFlow, safeAudioFlow } from './audio-flow';

const BUFFER_SAMPLE_COUNT = 8192;

class AudioRecorder {
  private readonly sampleBuffer = new ArrayBuffer(BUFFER_SAMPLE_COUNT * 2); // 2 bytes per sample

  private readonly sampleBufferView = new Uint8Array(this.sampleBuffer);

  private context: AudioContext | null = null;

  private decoderNode: AudioWorkletNode | null = null;

  private gainNode: GainNode | null = null;

  private sampleBufferOffset = 0;

  private mediaStreamSource: MediaStreamAudioSourceNode | null = null;

  private sampleRate: number;

  onaudiochunk: ((data: ArrayBuffer) => void) | undefined;

  muted = false;

  constructor(
    sampleRate: number = 24000,
    private audioFlow?: BrowserAudioFlow,
  ) {
    this.sampleRate = sampleRate;
    if (audioFlow)
      audioFlow.captureState = () => {
        const track = this.mediaStreamSource?.mediaStream.getAudioTracks()[0];
        return {
          context: this.context?.state ?? 'closed',
          enabled: track?.enabled ?? false,
          muted: track?.muted ?? false,
          ended: track?.readyState === 'ended',
          applicationMuted: this.muted,
        };
      };
  }

  async startRecording(deviceId?: string) {
    this.context = makeAudioContext();
    safeAudioFlow(() => this.audioFlow?.captureStarted());
    await this.context.audioWorklet.addModule(LibSampleRateSrc);
    await this.context.audioWorklet.addModule(PcmAudioWorkletSrc);
    this.decoderNode = new AudioWorkletNode(this.context, 'pcm-audio-worklet', {
      processorOptions: {
        outputSampleRate: this.sampleRate,
      },
    });
    this.gainNode = this.context.createGain();
    this.gainNode.gain.value = 0.0;
    this.gainNode.connect(this.context.destination);
    this.decoderNode.connect(this.gainNode);

    // Note: We don't use the sampleRate constraint here because Firefox cannot
    // use a MediaStreamAudioSourceNode with a sample rate different from the
    // context's sample rate. Instead, we resample the audio data to the desired
    // sample rate in the AudioWorkletProcessor.
    const audioStream = await navigator.mediaDevices.getUserMedia({
      audio: {
        deviceId: deviceId ? { exact: deviceId } : undefined,
      },
      video: false,
    });

    this.mediaStreamSource = this.context.createMediaStreamSource(audioStream);
    this.mediaStreamSource.connect(this.decoderNode);

    this.sampleBufferOffset = 0;

    this.decoderNode.port.onmessage = (event) => {
      safeAudioFlow(() =>
        this.audioFlow?.capture(event.data.chunk, this.muted),
      );
      if (!this.muted) {
        let eventOffset = 0;

        // Currently AudioWorkletProcessors are invoked with a maximum buffer
        // size of 128 frames. We accumulate the output samples in a buffer and
        // send them to the WebSocket server in larger chunks to improve
        // performance.
        while (eventOffset < event.data.chunk.byteLength) {
          const nBytesToCopy = Math.min(
            event.data.chunk.byteLength - eventOffset,
            this.sampleBuffer.byteLength - this.sampleBufferOffset,
          );
          this.sampleBufferView.set(
            new Uint8Array(event.data.chunk, eventOffset, nBytesToCopy),
            this.sampleBufferOffset,
          );
          this.sampleBufferOffset += nBytesToCopy;
          eventOffset += nBytesToCopy;

          if (this.sampleBufferOffset === this.sampleBuffer.byteLength) {
            if (this.audioFlow)
              this.audioFlow.counters.emittedBytes +=
                this.sampleBuffer.byteLength;
            this.onaudiochunk?.(this.sampleBuffer);
            this.sampleBufferOffset = 0;
          }
        }
      }
    };
  }

  stopRecording() {
    if (this.mediaStreamSource) {
      this.mediaStreamSource.mediaStream.getTracks().forEach((track) => {
        track.stop();
      });
      this.mediaStreamSource.disconnect();
      this.mediaStreamSource = null;
    }

    if (this.decoderNode) {
      this.decoderNode.disconnect();
      this.decoderNode = null;
    }

    if (this.gainNode) {
      this.gainNode.disconnect();
      this.gainNode = null;
    }

    if (this.context) {
      this.context.close();
      this.context = null;
    }
  }

  destroy() {
    this.stopRecording();
  }
}

export default AudioRecorder;
